import { z } from 'zod';
import type { Currency } from '../money/money';
import { moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Work order model (PRD → Maintenance → Sources of work, Flow; gitaumoses4/chukua#164). The ticket body and
 * the PRD name the states below; the grooming note's proposed/approved/…/closed list is not used.
 */

export const WORK_ORDER_SOURCES = ['scheduled_service', 'defect', 'road_incident', 'inspection_finding'] as const;
export type WorkOrderSource = (typeof WORK_ORDER_SOURCES)[number];

export const WORK_ORDER_SEVERITIES = ['critical', 'scheduled', 'monitor'] as const;
export type WorkOrderSeverity = (typeof WORK_ORDER_SEVERITIES)[number];

export const WORK_ORDER_STATES = ['raised', 'approved', 'scheduled', 'in_progress', 'completed', 'verified_closed', 'cancelled'] as const;
export type WorkOrderState = (typeof WORK_ORDER_STATES)[number];

/** `POST /work-orders` — the estimate is what the approval ladder reads, so it is required; the actual cost is #170's. */
export const createWorkOrderInputSchema = z
  .object({
    truckId: z.uuid(),
    source: z.enum(WORK_ORDER_SOURCES),
    sourceRef: z.string().trim().min(1).max(100).optional(),
    severity: z.enum(WORK_ORDER_SEVERITIES),
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    estimatedCost: moneySchema.refine((money) => money.amountMinor >= 0, { message: 'Estimate cannot be negative', path: ['amountMinor'] }),
  })
  .strict();
export type CreateWorkOrderInput = z.infer<typeof createWorkOrderInputSchema>;

export const scheduleWorkOrderInputSchema = z.object({ scheduledFor: z.iso.date() }).strict();
export type ScheduleWorkOrderInput = z.infer<typeof scheduleWorkOrderInputSchema>;

export const cancelWorkOrderInputSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type CancelWorkOrderInput = z.infer<typeof cancelWorkOrderInputSchema>;

/** `POST /work-orders/:id/resolve-by` — a monitor item closed because a service order on the same truck covered it. */
export const resolveWorkOrderInputSchema = z.object({ resolvingWorkOrderId: z.uuid() }).strict();
export type ResolveWorkOrderInput = z.infer<typeof resolveWorkOrderInputSchema>;

export const workOrderListQuerySchema = defineListQuery({
  sortFields: ['sequenceNumber', 'raisedAt', 'state', 'severity', 'estimatedCost'],
  defaultSort: { sort: 'raisedAt', order: 'desc' },
  filters: {
    truckId: listFilter(z.uuid()),
    state: listFilter(z.enum(WORK_ORDER_STATES)),
    severity: listFilter(z.enum(WORK_ORDER_SEVERITIES)),
    source: listFilter(z.enum(WORK_ORDER_SOURCES)),
  },
});
export type WorkOrderListQuery = z.infer<typeof workOrderListQuerySchema>;

export interface WorkOrderView {
  id: string;
  /** Shown as `WO-<n>`. */
  sequenceNumber: number;
  truckId: string;
  source: WorkOrderSource;
  sourceRef: string | null;
  severity: WorkOrderSeverity;
  title: string;
  description: string | null;
  estimatedCost: { amountMinor: number; currency: Currency };
  state: WorkOrderState;
  raisedById: string;
  raisedAt: string;
  approvedById: string | null;
  approvedAt: string | null;
  scheduledFor: string | null;
  startedAt: string | null;
  completedAt: string | null;
  verifiedById: string | null;
  verifiedAt: string | null;
  cancelReason: string | null;
  resolvedByWorkOrderId: string | null;
}

export interface WorkOrderTransitionView {
  id: string;
  workOrderId: string;
  fromState: WorkOrderState | null;
  toState: WorkOrderState;
  actorId: string;
  reason: string | null;
  occurredAt: string;
}
